import { useEffect, useState } from "react";
import {
  Users, ClipboardList, CalendarCheck2, CheckCircle2,
  Clock3, Bell, Flame, Building2, ChevronRight, TrendingUp,
  Activity, PieChart, MapPin
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../../api/axios";
import { useAdminAuth } from "../../context/AdminAuthContext";
import AdminNavbar from "../../components/layout/AdminNavbar";

/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
   PURE SVG CHART COMPONENTS (zero dependencies)
   â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */

// â”€â”€ Animated Donut Chart â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const DonutChart = ({ value, total, label, color = "#7c3aed", size = 120 }) => {
  const [animatedValue, setAnimatedValue] = useState(0);
  const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
  const radius = (size - 16) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (animatedValue / 100) * circumference;

  useEffect(() => {
        document.title = "Dashboard | Savrion Technologies";
    const timer = setTimeout(() => setAnimatedValue(percentage), 100);
    return () => clearTimeout(timer);
  }, [percentage]);

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          {/* Background ring */}
          <circle
            cx={size / 2} cy={size / 2} r={radius}
            fill="none"
            stroke="var(--border-color)"
            strokeWidth="8"
            opacity="0.3"
          />
          {/* Animated value ring */}
          <circle
            cx={size / 2} cy={size / 2} r={radius}
            fill="none"
            stroke={color}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            style={{ transition: "stroke-dashoffset 1.2s cubic-bezier(0.4, 0, 0.2, 1)" }}
          />
        </svg>
        {/* Center text */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-xl font-black tracking-tight">{animatedValue}%</span>
        </div>
      </div>
      <div className="text-center">
        <p className="text-[10px] font-black text-[var(--text-secondary)] uppercase tracking-wider">{label}</p>
        <p className="text-[10px] font-bold text-[var(--text-primary)] mt-0.5">{value} / {total}</p>
      </div>
    </div>
  );
};

// â”€â”€ Weekly Attendance Line Chart â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const WeeklyLineChart = ({ data, height = 180 }) => {
  const [animated, setAnimated] = useState(false);
  const maxValue = Math.max(...data.map(d => d.value), 1);
  const padding = { top: 25, right: 20, bottom: 35, left: 35 };
  const chartWidth = 500;
  const chartHeight = height;
  const plotWidth = chartWidth - padding.left - padding.right;
  const plotHeight = chartHeight - padding.top - padding.bottom;

  useEffect(() => {
        document.title = "Dashboard | Savrion Technologies";
    const timer = setTimeout(() => setAnimated(true), 200);
    return () => clearTimeout(timer);
  }, []);

  const points = data.map((d, i) => ({
    x: padding.left + (i / Math.max(data.length - 1, 1)) * plotWidth,
    y: padding.top + plotHeight - (d.value / maxValue) * plotHeight,
    ...d
  }));

  const linePath = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
  const areaPath = `${linePath} L ${points[points.length - 1]?.x || 0} ${padding.top + plotHeight} L ${points[0]?.x || 0} ${padding.top + plotHeight} Z`;

  // Y-axis grid lines
  const gridLines = [0, 0.25, 0.5, 0.75, 1].map(frac => ({
    y: padding.top + plotHeight - frac * plotHeight,
    label: Math.round(frac * maxValue)
  }));

  return (
    <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} className="w-full" preserveAspectRatio="xMidYMid meet">
      <defs>
        <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--accent-primary)" stopOpacity="0.12" />
          <stop offset="100%" stopColor="var(--accent-primary)" stopOpacity="0.00" />
        </linearGradient>
      </defs>

      {/* Grid lines */}
      {gridLines.map((g, i) => (
        <g key={i}>
          <line
            x1={padding.left} y1={g.y}
            x2={chartWidth - padding.right} y2={g.y}
            stroke="var(--border-color)" strokeWidth="0.5" strokeDasharray="3 3"
          />
          <text x={padding.left - 8} y={g.y + 3} textAnchor="end"
            fill="var(--text-secondary)" fontSize="9" fontWeight="700">{g.label}</text>
        </g>
      ))}

      {/* Gradient area */}
      {animated && data.length > 1 && (
        <path d={areaPath} fill="url(#areaGradient)"
          style={{ opacity: animated ? 1 : 0, transition: "opacity 0.8s ease" }} />
      )}

      {/* Line */}
      {animated && data.length > 1 && (
        <path d={linePath} fill="none" stroke="var(--accent-primary)" strokeWidth="2.5"
          strokeLinecap="round" strokeLinejoin="round"
          style={{
            strokeDasharray: 2000, strokeDashoffset: animated ? 0 : 2000,
            transition: "stroke-dashoffset 1.5s cubic-bezier(0.4, 0, 0.2, 1)"
          }}
        />
      )}

      {/* Data points & labels */}
      {points.map((p, i) => (
        <g key={i}>
          {/* Dot */}
          <circle cx={p.x} cy={p.y} r={animated ? 4.5 : 0} fill="var(--accent-primary)"
            stroke="var(--card-bg)" strokeWidth="2"
            style={{ transition: `r 0.4s ease ${i * 0.08}s` }} />
          {/* Value label */}
          <text x={p.x} y={p.y - 10} textAnchor="middle"
            fill="var(--text-primary)" fontSize="9" fontWeight="800"
            style={{ opacity: animated ? 1 : 0, transition: `opacity 0.4s ease ${i * 0.08 + 0.4}s` }}>
            {p.value}
          </text>
          {/* Day label */}
          <text x={p.x} y={chartHeight - 8} textAnchor="middle"
            fill="var(--text-secondary)" fontSize="9" fontWeight="700">
            {p.day}
          </text>
        </g>
      ))}
    </svg>
  );
};

// â”€â”€ Horizontal Bar Chart â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const HorizontalBarChart = ({ data, height = 200 }) => {
  const [animated, setAnimated] = useState(false);
  const maxValue = Math.max(...data.map(d => d.value), 1);

  useEffect(() => {
        document.title = "Dashboard | Savrion Technologies";
    const timer = setTimeout(() => setAnimated(true), 300);
    return () => clearTimeout(timer);
  }, []);

  const barColors = ["#7c3aed", "#3b82f6", "#10b981", "#f59e0b", "#6366f1", "#ec4899"];

  return (
    <div className="space-y-3.5" style={{ minHeight: height }}>
      {data.map((item, i) => {
        const widthPercent = (item.value / maxValue) * 100;
        const color = barColors[i % barColors.length];
        return (
          <div key={i} className="flex items-center gap-3">
            <span className="text-[11px] font-bold text-[var(--text-secondary)] w-20 truncate capitalize text-right">
              {item.label}
            </span>
            <div className="flex-1 h-6 bg-[var(--bg-primary)] rounded-lg overflow-hidden border border-[var(--border-color)]">
              <div
                className="h-full rounded-lg flex items-center justify-end px-2.5 transition-all duration-1000 ease-out"
                style={{
                  width: animated ? `${Math.max(widthPercent, 8)}%` : "0%",
                  backgroundColor: color,
                  transitionDelay: `${i * 80}ms`
                }}
              >
                <span className="text-[9px] font-black text-white">
                  {item.value}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};


/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
   MAIN DASHBOARD COMPONENT
   â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */

const Dashboard = () => {
  const navigate = useNavigate();
  const { admin } = useAdminAuth();

  const [stats, setStats] = useState({
    employees: 0,
    activeEmployees: 0,
    tasks: 0,
    checkedIn: 0,
    checkedOut: 0,
    totalPresent: 0,
    completedTasks: 0,
    pendingTasks: 0,
    submittedEod: 0
  });
  const [weeklyChartData, setWeeklyChartData] = useState([]);
  const [departmentChartData, setDepartmentChartData] = useState([]);
  const [teamStats, setTeamStats] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState({
    pendingLeaves: 0,
    pendingExpenses: 0,
    attendanceCorrections: 0,
    profileChangeRequests: 0,
    payslipIssues: 0,
    missingEod: 0
  });
  const [pendingGrievancesCount, setPendingGrievancesCount] = useState(0);
  const [salesTracking, setSalesTracking] = useState({
    totalSalesEmployees: 0,
    activeTracking: 0,
    checkedIn: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
        document.title = "Dashboard | Savrion Technologies";
    let active = true;

    const fetchSummary = async () => {
      try {
        const summaryRes = await api.get("/api/admin/dashboard-summary");
        if (!active) return;
        setStats(summaryRes.data.stats || {});
        setWeeklyChartData(summaryRes.data.weeklyChartData || []);
        setDepartmentChartData(summaryRes.data.departmentChartData || []);
        setTeamStats(summaryRes.data.teamStats || []);
        setAnnouncements(summaryRes.data.announcements || []);
      } catch (err) {
        console.error("Error fetching dashboard summary:", err);
        if (err.response?.status === 401 || err.response?.status === 403) navigate("/admin/login");
      } finally {
        if (active) setLoading(false);
      }
    };

    const fetchSecondaryData = async () => {
      const [approvalInboxRes, complaintsRes, salesTrackingRes] = await Promise.all([
          api.get("/api/admin/approval-inbox").catch(err => {
            console.error("Error fetching approval inbox:", err);
            return { data: { counts: {} } };
          }),
          api.get("/api/admin/complaints", { params: { status: "Open", limit: 160 } }).catch(err => {
            console.error("Error fetching complaints:", err);
            return { data: [] };
          }),
          api.get("/api/admin/sales-live-tracking").catch(err => {
            console.error("Error fetching sales live tracking:", err);
            return { data: { totalSalesEmployees: 0, activeTracking: 0, checkedIn: 0 } };
          })
        ]);

        if (!active) return;
        if (approvalInboxRes.data && approvalInboxRes.data.counts) {
          setPendingApprovals(approvalInboxRes.data.counts);
        }
        setPendingGrievancesCount(complaintsRes.data?.length || 0);
        setSalesTracking({
          totalSalesEmployees: salesTrackingRes.data?.totalSalesEmployees || 0,
          activeTracking: salesTrackingRes.data?.activeTracking || 0,
          checkedIn: salesTrackingRes.data?.checkedIn || 0,
        });
    };

    fetchSummary();
    fetchSecondaryData();
    return () => {
      active = false;
    };
  }, [navigate]);

  return (
    <div className="flex flex-col xl:flex-row min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)]">
      <AdminNavbar />

      <main className="flex-1 min-w-0 w-full px-4 py-6 md:px-8 space-y-6 overflow-auto">

        {/* ===== WELCOME BANNER ===== */}
        <section className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-1">
          <div>
            <span className="rounded-full bg-[var(--accent-subtle)] px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider text-[var(--accent-primary)]">
              Admin Workspace
            </span>
            <h1 className="mt-1 text-2xl font-black tracking-tight md:text-3xl text-[var(--text-primary)]">
              Welcome back, {admin?.name || "Super Admin"}
            </h1>
            <p className="mt-1.5 text-xs text-[var(--text-secondary)] font-semibold">
              Here is a cross-functional overview of your workforce metrics today.
            </p>
          </div>
        </section>

        {/* ===== KPI CARDS ===== */}
        <section className="grid grid-cols-2 xl:grid-cols-4 gap-4">
          {[
            { title: "Total Employees", value: stats.employees, icon: <Users size={20} />, color: "text-[var(--accent-primary)] dark:text-[var(--accent-primary)]", bg: "bg-[var(--accent-primary)]/10 border-[var(--accent-primary)]/20" },
            { title: "Checked In Today", value: stats.checkedIn, icon: <CalendarCheck2 size={20} />, color: "text-emerald-600 dark:text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/20" },
            { title: "Total Tasks", value: stats.tasks, icon: <ClipboardList size={20} />, color: "text-[var(--accent-primary)] dark:text-[var(--accent-primary)]", bg: "bg-[var(--accent-subtle)] border-[var(--accent-primary)]/20" },
            { title: "EOD Submitted", value: stats.submittedEod, icon: <CheckCircle2 size={20} />, color: "text-pink-600 dark:text-pink-400", bg: "bg-pink-500/10 border-pink-500/20" },
          ].map((item, i) => (
            <div key={i} className="rounded-3xl bg-[var(--card-bg)] p-5 border border-[var(--border-color)] hover:border-[var(--accent-primary)]/30 hover:shadow-md transition-all duration-300 group flex items-center justify-between">
              <div className="space-y-1.5 min-w-0">
                <p className="text-[10px] font-black text-[var(--text-secondary)] uppercase tracking-wider truncate">{item.title}</p>
                <h2 className={`text-3xl font-black tracking-tight text-[var(--text-primary)] leading-none ${loading && item.value == null ? "animate-pulse" : ""}`}>
                  {item.value ?? (loading ? "..." : 0)}
                </h2>
              </div>
              <div className={`w-12 h-12 rounded-2xl border ${item.bg} ${item.color} flex items-center justify-center shrink-0`}>
                {item.icon}
              </div>
            </div>
          ))}
        </section>

        {/* ===== SALES LIVE TRACKING ===== */}
        <section
          onClick={() => navigate("/admin/sales-live-tracking")}
          className="group cursor-pointer rounded-3xl border border-emerald-500/20 bg-gradient-to-r from-emerald-500/10 via-[var(--card-bg)] to-violet-500/10 p-5 shadow-sm transition-all hover:border-emerald-500/40 hover:shadow-md"
        >
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-emerald-500/20 bg-emerald-500/10 text-emerald-600">
                <MapPin size={22} />
              </div>
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-emerald-600">Live sales tracking</p>
                <h2 className="mt-1 text-xl font-black tracking-tight">Monitor sales team field locations</h2>
                <p className="mt-1 text-xs font-semibold text-[var(--text-secondary)]">
                  View current GPS pings for checked-in sales employees.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 md:min-w-[320px]">
              <MiniLiveMetric label="Sales" value={salesTracking.totalSalesEmployees} />
              <MiniLiveMetric label="Checked in" value={salesTracking.checkedIn} />
              <MiniLiveMetric label="Live now" value={salesTracking.activeTracking} highlight />
            </div>
          </div>
        </section>

        {/* ===== ANALYTICS ROW ===== */}
        <section className="grid grid-cols-1 xl:grid-cols-3 gap-6">

          {/* WEEKLY ATTENDANCE TREND */}
          <div className="xl:col-span-2 rounded-3xl bg-[var(--card-bg)] p-6 border border-[var(--border-color)] shadow-sm">
            <div className="flex items-center justify-between mb-5 pb-3 border-b border-[var(--border-color)]">
              <div>
                <h2 className="text-sm font-black uppercase tracking-wider text-[var(--text-primary)] flex items-center gap-2">
                  <Activity size={16} className="text-[var(--accent-primary)]" /> Weekly Attendance Trend
                </h2>
                <p className="text-xs text-[var(--text-secondary)] font-semibold mt-0.5">Check-ins over the last 7 days</p>
              </div>
              <span className="text-[9px] font-black text-[var(--accent-primary)] bg-[var(--accent-subtle)] border border-[var(--accent-primary)]/20 px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                Active Week
              </span>
            </div>
            <WeeklyLineChart data={weeklyChartData} height={200} />
          </div>

          {/* ATTENDANCE RATE */}
          <div className="rounded-3xl bg-[var(--card-bg)] p-6 border border-[var(--border-color)] shadow-sm">
            <div className="flex items-center justify-between mb-5 pb-3 border-b border-[var(--border-color)]">
              <div>
                <h2 className="text-sm font-black uppercase tracking-wider text-[var(--text-primary)] flex items-center gap-2">
                  <PieChart size={16} className="text-emerald-500" /> Attendance Rate
                </h2>
                <p className="text-xs text-[var(--text-secondary)] font-semibold mt-0.5">Today's metrics snapshot</p>
              </div>
            </div>
            <div className="flex flex-col items-center justify-center gap-5">
              <DonutChart
                value={stats.totalPresent}
                total={stats.activeEmployees}
                label="Present Today"
                color="#10b981"
                size={120}
              />
              <div className="grid grid-cols-2 gap-3 w-full">
                <div className="rounded-2xl bg-emerald-500/5 border border-emerald-500/15 p-3 text-center">
                  <p className="text-lg font-black text-emerald-600 dark:text-emerald-400">{stats.checkedIn}</p>
                  <p className="text-[9px] font-black text-emerald-600/70 dark:text-emerald-400/60 uppercase tracking-widest">Working</p>
                </div>
                <div className="rounded-2xl bg-[var(--accent-primary)]/5 border border-blue-500/15 p-3 text-center">
                  <p className="text-lg font-black text-[var(--accent-primary)] dark:text-[var(--accent-primary)]">{stats.checkedOut}</p>
                  <p className="text-[9px] font-black text-[var(--accent-primary)]/70 dark:text-[var(--accent-primary)]/60 uppercase tracking-widest">Checked Out</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== MIDDLE ROW (Task Productivity, Dept Check-ins, Alerts & Announcements) ===== */}
        <section className="grid grid-cols-1 xl:grid-cols-3 gap-6">

          {/* LEFT SIDE (Department Check-ins & Task Productivity) */}
          <div className="xl:col-span-2 space-y-6">

            {/* Department Bar Chart */}
            <div className="rounded-3xl bg-[var(--card-bg)] p-6 border border-[var(--border-color)] shadow-sm">
              <div className="flex items-center justify-between mb-5 pb-3 border-b border-[var(--border-color)]">
                <div>
                  <h2 className="text-sm font-black uppercase tracking-wider text-[var(--text-primary)] flex items-center gap-2">
                    <Building2 size={16} className="text-[var(--accent-primary)]" /> Department Check-ins Today
                  </h2>
                  <p className="text-xs text-[var(--text-secondary)] font-semibold mt-0.5">Team attendance distribution</p>
                </div>
                <span className="text-[9px] font-black text-[var(--accent-primary)] bg-[var(--accent-primary)]/10 border border-[var(--accent-primary)]/20 px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                  Live
                </span>
              </div>
              <HorizontalBarChart data={departmentChartData} height={180} />
            </div>

            {/* TASK PRODUCTIVITY */}
            <div className="rounded-3xl bg-[var(--card-bg)] p-6 border border-[var(--border-color)] shadow-sm">
              <div className="flex items-center justify-between mb-5 pb-3 border-b border-[var(--border-color)]">
                <div>
                  <h2 className="text-sm font-black uppercase tracking-wider text-[var(--text-primary)] flex items-center gap-2">
                    <TrendingUp size={16} className="text-[var(--accent-primary)]" /> Task Productivity
                  </h2>
                  <p className="text-xs text-[var(--text-secondary)] font-semibold mt-0.5">Live project execution metrics</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                <div className="space-y-3">
                  {[
                    { label: "Pending Tasks", value: stats.pendingTasks, icon: <Clock3 size={18} className="text-amber-500" />, accent: false },
                    { label: "Completed", value: stats.completedTasks, icon: <Flame size={18} className="text-orange-500" />, accent: false },
                    { label: "Total Volume", value: stats.tasks, icon: <ClipboardList size={18} className="text-[var(--accent-primary)]" />, accent: true },
                  ].map((item, i) => (
                    <div key={i} className={`rounded-xl p-3 border flex items-center gap-4 ${item.accent ? "bg-[var(--accent-subtle)]/50 border-[var(--accent-primary)]/20" : "bg-[var(--bg-primary)] border-[var(--border-color)]"}`}>
                      <div className="shrink-0">{item.icon}</div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-[9px] font-black uppercase tracking-widest ${item.accent ? "text-[var(--accent-primary)] dark:text-[var(--accent-primary)]" : "text-[var(--text-secondary)]"}`}>{item.label}</p>
                      </div>
                      <h3 className={`text-xl font-black ${item.accent ? "text-[var(--accent-primary)] dark:text-[var(--accent-primary)]" : "text-[var(--text-primary)]"}`}>{item.value}</h3>
                    </div>
                  ))}
                </div>

                <div className="flex flex-col items-center justify-center border-t md:border-t-0 md:border-l border-[var(--border-color)] pt-4 md:pt-0 md:pl-6">
                  <DonutChart
                    value={stats.submittedEod}
                    total={stats.activeEmployees}
                    label="EOD Compliance"
                    color="#7c3aed"
                    size={100}
                  />
                </div>
              </div>
            </div>

          </div>

          {/* RIGHT SIDE (Pending Action Items & Announcements) */}
          <div className="xl:col-span-1 space-y-6">

            {/* PENDING ACTION ITEMS */}
            <div className="rounded-3xl bg-[var(--card-bg)] p-6 border border-[var(--border-color)] shadow-sm">
              <div className="flex items-center justify-between mb-4 pb-3 border-b border-[var(--border-color)]">
                <div>
                  <h2 className="text-sm font-black uppercase tracking-wider text-[var(--text-primary)] flex items-center gap-2">
                    <Bell size={16} className="text-amber-500" /> Pending Action Items
                  </h2>
                  <p className="text-xs text-[var(--text-secondary)] font-semibold mt-0.5 font-medium">Items requiring admin review</p>
                </div>
              </div>
              
              <div className="space-y-2.5 max-h-[180px] overflow-y-auto pr-1 scrollbar-thin">
                {(() => {
                  const items = [
                    { count: pendingApprovals.pendingLeaves, label: 'Leave Request', color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20', path: '/admin/approval-inbox' },
                    { count: pendingApprovals.pendingExpenses, label: 'Expense Claim', color: 'bg-[var(--accent-primary)]/10 text-[var(--accent-primary)] dark:text-[var(--accent-primary)] border-[var(--accent-primary)]/20', path: '/admin/approval-inbox' },
                    { count: pendingApprovals.attendanceCorrections, label: 'Attendance Correction', color: 'bg-[var(--accent-subtle)] text-[var(--accent-primary)] dark:text-[var(--accent-primary)] border-[var(--accent-primary)]/20', path: '/admin/approval-inbox' },
                    { count: pendingApprovals.profileChangeRequests, label: 'Profile Change Request', color: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-500/20', path: '/admin/approval-inbox' },
                    { count: pendingApprovals.payslipIssues, label: 'Payslip Issue', color: 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20', path: '/admin/approval-inbox' },
                    { count: pendingGrievancesCount, label: 'Open Grievance / Request', color: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20', path: '/admin/complaints' }
                  ].filter(item => item.count > 0);

                  if (items.length === 0) {
                    return (
                      <div className="flex flex-col items-center justify-center py-6 text-center bg-emerald-500/5 border border-emerald-500/15 rounded-2xl p-4">
                        <div className="w-9 h-9 rounded-full bg-emerald-500/15 text-emerald-600 flex items-center justify-center mb-2">
                          <CheckCircle2 size={18} />
                        </div>
                        <p className="text-xs font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">All Caught Up</p>
                        <p className="text-[10px] text-[var(--text-secondary)] font-semibold mt-0.5">No pending approvals or grievances</p>
                      </div>
                    );
                  }

                  return items.map((item, index) => (
                    <div
                      key={index}
                      onClick={() => navigate(item.path)}
                      className={`group flex items-center justify-between p-3 rounded-2xl border ${item.color} cursor-pointer hover:shadow-sm transition-all duration-300`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-[11px] font-black tracking-tight">
                          {item.count} {item.label}{item.count > 1 ? 's' : ''}
                        </span>
                      </div>
                      <ChevronRight size={14} className="opacity-60 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  ));
                })()}
              </div>
            </div>

            {/* ANNOUNCEMENTS */}
            <div className="rounded-3xl bg-[var(--card-bg)] p-6 border border-[var(--border-color)] shadow-sm">
              <div className="flex items-center justify-between mb-5 pb-3 border-b border-[var(--border-color)]">
                <div>
                  <h2 className="text-sm font-black uppercase tracking-wider text-[var(--text-primary)] flex items-center gap-2">
                    <Bell size={16} className="text-amber-500" /> Announcements
                  </h2>
                  <p className="text-xs text-[var(--text-secondary)] font-semibold mt-0.5">Latest company updates</p>
                </div>
              </div>
              <div className="space-y-3 overflow-y-auto max-h-48 scrollbar-thin">
                {announcements.length === 0 ? (
                  <p className="text-xs text-[var(--text-secondary)] text-center py-6 italic">No recent announcements.</p>
                ) : (
                  announcements.slice(0, 4).map(a => (
                    <div
                      key={a._id}
                      onClick={() => navigate("/admin/announcements")}
                      className="group rounded-2xl bg-[var(--bg-primary)] border border-[var(--border-color)] p-3.5 hover:border-[var(--accent-primary)]/40 hover:shadow-sm transition-all cursor-pointer flex items-center justify-between gap-4"
                    >
                      <div className="min-w-0 flex-1 space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 rounded bg-[var(--accent-subtle)] border border-[var(--accent-primary)]/20 text-[var(--accent-primary)] dark:text-[var(--accent-primary)] text-[8px] font-black uppercase tracking-widest">
                            {a.category}
                          </span>
                        </div>
                        <h3 className="font-bold text-xs text-[var(--text-primary)] leading-snug">{a.title}</h3>
                        <p className="text-[10px] text-[var(--text-secondary)] font-semibold line-clamp-1">{a.body}</p>
                      </div>
                      <ChevronRight size={14} className="text-gray-400 group-hover:text-[var(--accent-primary)] transition-colors shrink-0" />
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </section>

        {/* ===== DEPARTMENT OVERVIEW ===== */}
        <section className="rounded-3xl bg-[var(--card-bg)] p-6 border border-[var(--border-color)] shadow-sm">
          <div className="flex items-center justify-between mb-5 pb-3 border-b border-[var(--border-color)]">
            <div>
              <h2 className="text-sm font-black uppercase tracking-wider text-[var(--text-primary)] flex items-center gap-2">
                <Building2 size={16} className="text-[var(--accent-primary)]" /> Department Overview
              </h2>
              <p className="text-xs text-[var(--text-secondary)] font-semibold mt-0.5">Cross-functional team insights</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
            {teamStats.map(team => (
              <div key={team.name} className="rounded-2xl bg-[var(--bg-primary)] border border-[var(--border-color)] p-4 hover:border-[var(--accent-primary)]/30 hover:shadow-sm transition-all group">
                <h3 className="capitalize font-black text-xs text-[var(--text-primary)] mb-3 tracking-wide">{team.name}</h3>
                <div className="space-y-2">
                  <div className="flex justify-between items-center text-[10px] border-b border-[var(--border-color)] pb-1.5 font-semibold">
                    <span className="text-[var(--text-secondary)]">Total</span>
                    <span className="font-extrabold text-[var(--text-primary)]">{team.total}</span>
                  </div>
                  <div className="flex justify-between items-center text-[10px] border-b border-[var(--border-color)] pb-1.5 font-semibold">
                    <span className="text-[var(--text-secondary)]">Active</span>
                    <span className="font-extrabold text-emerald-600 dark:text-emerald-400">{team.active}</span>
                  </div>
                  <div className="flex justify-between items-center text-[10px] font-semibold">
                    <span className="text-[var(--text-secondary)]">Checked In</span>
                    <span className="font-extrabold text-[var(--accent-primary)] dark:text-[var(--accent-primary)]">{team.checkedIn}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

      </main>
    </div>
  );
};

const MiniLiveMetric = ({ label, value, highlight = false }) => (
  <div className={`rounded-2xl border p-3 text-center ${
    highlight
      ? "border-emerald-500/25 bg-emerald-500/10 text-emerald-700"
      : "border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)]"
  }`}>
    <p className="text-2xl font-black leading-none">{value}</p>
    <p className="mt-1 text-[9px] font-black uppercase tracking-widest text-[var(--text-secondary)]">{label}</p>
  </div>
);

export default Dashboard;


